import { decodeSimHour, formatSimDate, formatSimHour } from '../../domain/demand/simClock';
import { formatUsd } from '../format';
import { SaveMenu } from './SaveMenu';
import type { SimulationControls, SimulationUIState } from '../hooks/useSimulationWorker';

const SPEED_OPTIONS = [1, 5, 24] as const;

interface HeaderProps {
  state: SimulationUIState;
  controls: SimulationControls;
}

export function Header({ state, controls }: HeaderProps) {
  const clock = decodeSimHour(state.hour);
  const ledger = state.ledger;
  const net = ledger === null ? 0 : ledger.revenueTodayUSD - ledger.operatingCostTodayUSD;

  return (
    <header className="header">
      <div className="brand">
        <span className="brand-mark" aria-hidden="true">
          ✈
        </span>
        <span className="brand-name">Flightline</span>
      </div>

      <div className="controls" role="group" aria-label="Simulation controls">
        <button
          type="button"
          className={`btn btn-primary${state.running ? ' is-running' : ''}`}
          onClick={state.running ? controls.pause : controls.start}
          disabled={!state.ready}
          aria-label={state.running ? 'Pause simulation' : 'Start simulation'}
        >
          {state.running ? '❚❚ Pause' : '▶ Play'}
        </button>
        <div className="segmented" role="radiogroup" aria-label="Simulation speed">
          {SPEED_OPTIONS.map((speed) => (
            <button
              key={speed}
              type="button"
              role="radio"
              aria-checked={state.speedMultiplier === speed}
              className={`seg${state.speedMultiplier === speed ? ' is-active' : ''}`}
              onClick={() => controls.setSpeed(speed)}
              disabled={!state.ready}
            >
              {speed}x
            </button>
          ))}
        </div>
      </div>

      <SaveMenu state={state} controls={controls} />

      <div className="clock" aria-live="off">
        <div className="clock-date">{formatSimDate(clock)}</div>
        <div className="clock-time">
          {formatSimHour(clock.hourOfDay)} <span className="muted">UTC · Day {clock.day + 1}</span>
        </div>
      </div>

      <dl className="ledger">
        <div className="stat">
          <dt>Cash</dt>
          <dd className={ledger !== null && ledger.cashUSD < 0 ? 'neg' : ''}>
            {ledger === null ? '—' : formatUsd(ledger.cashUSD)}
          </dd>
        </div>
        <div className="stat">
          <dt>Revenue today</dt>
          <dd>{ledger === null ? '—' : formatUsd(ledger.revenueTodayUSD)}</dd>
        </div>
        <div className="stat">
          <dt>Operating cost today</dt>
          <dd>{ledger === null ? '—' : formatUsd(ledger.operatingCostTodayUSD)}</dd>
        </div>
        <div className="stat">
          <dt>Net today</dt>
          <dd className={net < 0 ? 'neg' : 'pos'}>{ledger === null ? '—' : formatUsd(net)}</dd>
        </div>
      </dl>
    </header>
  );
}
